<script lang="ts">
import { defineComponent, h, type PropType } from 'vue'

import AppBadge from './AppBadge.vue'
import { featherContents, featherName } from './icons'

/**
 * A Feather icon by its legacy name (`MonitorIcon`) or Feather name (`monitor`), with an optional counter badge on its
 * corner (legacy `feather-icon :badge`, e.g. the navbar mail icon). Hidden from screen readers unless `label` is set.
 */
export default defineComponent({
  name: 'AppIcon',
  props: {
    name: { type: String, required: true },
    /** Pixels. */
    size: { type: [Number, String] as PropType<number | string>, default: 14 },
    /** Counter on the top-right corner; not shown when 0 or empty. */
    badge: { type: [Number, String] as PropType<number | string | null>, default: null },
    badgeVariant: {
      type: String as PropType<'danger' | 'primary' | 'success' | 'warning' | 'info'>,
      default: 'danger',
    },
    /** Accessible name; without it the icon is decorative. */
    label: { type: String, default: '' },
  },
  setup(props) {
    return () => {
      const contents = featherContents(props.name)
      const size = Number(props.size)
      const svg = h('svg', {
        xmlns: 'http://www.w3.org/2000/svg',
        width: size,
        height: size,
        viewBox: '0 0 24 24',
        fill: 'none',
        stroke: 'currentColor',
        'stroke-width': 2,
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
        class: ['feather', `feather-${featherName(props.name)}`],
        role: props.label ? 'img' : undefined,
        'aria-label': props.label || undefined,
        'aria-hidden': props.label ? undefined : 'true',
        // Static markup from the feather-icons package (no user input).
        innerHTML: contents ?? '',
      })
      const showBadge = props.badge !== null && props.badge !== '' && Number(props.badge) !== 0
      if (!showBadge) return svg
      return h('span', { class: 'app-icon-with-badge relative inline-flex' }, [
        svg,
        h(AppBadge, {
          value: props.badge ?? undefined,
          variant: props.badgeVariant,
          class: 'app-icon-badge absolute -top-2 -right-2',
          'data-testid': 'icon-badge',
        }),
      ])
    }
  },
})
</script>
